import { useEffect, useState } from "react";
import { Alert, Button, Card, CardBody, Col, Spinner } from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/api/api.js";
import { formatDate } from "@/utils/formatDate.js";

/** Cada cuánto se vuelve a preguntar, y cuántas veces antes de dejar de esperar. */
const POLL_MS = 3000;
const MAX_POLLS = 20;

/**
 * Si ya hay algo que decirle al usuario. Una referencia SPEI se da por
 * contestada de inmediato: el pago llegará en horas, no en segundos.
 */
const settled = (ticket) =>
  ticket?.approvalStatus === 1 ||
  ticket?.paymentOrder?.status === "expirada" ||
  ticket?.paymentOrder?.channel === "spei";

/**
 * A donde regresa Conekta después de la página de pago.
 *
 * Que el navegador haya vuelto no prueba nada: los folios los acredita el aviso
 * de Conekta al servidor. Esta pantalla sólo pregunta cómo va la compra hasta
 * que el servidor la da por pagada, y si tarda, dice que no hace falta esperar.
 */
const PaymentResult = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const ticketId = params.get("ticket");
  const failed = params.get("fallo") !== null;
  const [ticket, setTicket] = useState(null);
  const [polls, setPolls] = useState(0);

  useEffect(() => {
    if (!ticketId || failed) return undefined;

    let active = true;
    let timer;
    const load = async (attempt) => {
      let data = null;
      try {
        ({ data } = await api.get(`/request_tickets/${ticketId}`));
      } catch {
        /* se reintenta en la siguiente vuelta */
      }
      if (!active) return;
      if (data) setTicket(data);
      setPolls(attempt);
      if (!settled(data) && attempt < MAX_POLLS) {
        timer = setTimeout(() => load(attempt + 1), POLL_MS);
      }
    };

    load(1);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [ticketId, failed]);

  const order = ticket?.paymentOrder;
  const paid = ticket?.approvalStatus === 1;

  let content;
  if (failed) {
    content = <Alert variant="warning">{t("packages.resultFailed")}</Alert>;
  } else if (paid) {
    content = (
      <Alert variant="success">
        {t("packages.resultPaid", {
          count: ticket.quantityTickets,
          name: ticket.packageName,
        })}
      </Alert>
    );
  } else if (order?.status === "expirada") {
    content = <Alert variant="warning">{t("packages.resultExpired")}</Alert>;
  } else if (order?.channel === "spei") {
    content = (
      <Alert variant="info">
        <p>{t("packages.resultSpei", { date: formatDate(order.expiresAt) })}</p>
        <Button variant="primary" href={order.checkoutUrl}>
          {t("packages.seeSpeiReference")}
        </Button>
      </Alert>
    );
  } else if (polls >= MAX_POLLS) {
    content = <Alert variant="info">{t("packages.resultWaitingLong")}</Alert>;
  } else {
    content = (
      <div className="d-flex align-items-center gap-2 text-muted">
        <Spinner animation="border" size="sm" />
        {t("packages.resultWaiting")}
      </div>
    );
  }

  return (
    <Col xs={12}>
      <Card>
        <CardBody>
          {content}
          <div className="d-flex gap-2 mt-3">
            <Button as={Link} to="/buy_package" variant="outline-secondary">
              {t("packages.backToPackages")}
            </Button>
            <Button as={Link} to="/dashboard" variant="primary">
              {t("packages.goHome")}
            </Button>
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};

export default PaymentResult;
